'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebouncedEffect } from 'src/components/list/hooks/useDebouncedEffect'
import { applyFilterFacets, getFacetRequestKey } from 'src/components/list/helpers/filterFacets'
import { getListQueryParamName } from 'src/_helpers/listQueryParams'
import {
  getLocalizedParamValues,
  resolvePageFromParamValue,
  resolveSortValueFromParamValue,
  resolveTabIdFromParamValue,
} from 'src/components/list/helpers/listStateFromParams'
import {
  collectFilterDescriptors,
  readFiltersFromSearch,
  writeFiltersToParams,
} from 'src/components/list/helpers/listFiltersUrl'

/**
 * Uniwersalny hook do "list z paginacją", które:
 * - pobierają dane z API po stronie klienta (fetcher),
 * - mają sortowanie + paginację,
 * - opcjonalnie mają zakładki (tabs),
 * - opcjonalnie mają filtry (tekstowe, multi, single, toggle),
 * - mapują "surowe" elementy z API do kafelków/elementów UI.
 *
 * Jak dodać nową listę w projekcie:
 * 1) Utwórz plik `*ListDefinition.js` z obiektem definicji (perPage, sortOptions, fetcher, buildParams, selectResults, mapToItem, filterSchema).
 * 2) W kliencie wywołaj `usePagedRemoteList({ ...definition, locale, initialItems, initialTotalItems, filterOptionsContext })`.
 * 3) Jeśli lista ma filtry, podepnij `<FiltersPanel schema={definition.filterSchema} optionsById={filterOptionsById} adapter={filtersAdapter} />`.
 * 4) Użyj wspólnych klocków UI: `<ListSortControls ... />` + `<PagedTileGrid ... />`.
 *
 * Cel: w każdym nowym widoku listy piszesz tylko mapowanie parametrów/odpowiedzi API i (opcjonalnie) schemat filtrów,
 * a cała logika paginacji, debounce, stan filtrów, sort, loading/empty UI jest współdzielona.
 */
const asArray = (value) => (Array.isArray(value) ? value : [])

/**
 * Buduje początkowy stan filtrów na podstawie `filterSchema`.
 * Uwaga: typy wartości:
 * - `textValues[id]` => string
 * - `multiValues[id]` => Set
 * - `singleValues[id]` => string
 * - `booleanValues[id]` => boolean
 */
const createInitialFilterState = (schema, initialOverrides) => {
  const textValues = {}
  const multiValues = {}
  const singleValues = {}
  const booleanValues = {}

  const initializeFilter = (item) => {
    if (!item) return
    const id = item.id
    if (!id) return

    switch (item.control) {
      case 'text':
        textValues[id] = item.defaultValue ?? ''
        break
      case 'multi':
      case 'searchable-multi':
      case 'pill-multi':
      case 'select-multi':
        multiValues[id] = new Set(asArray(item.defaultValue))
        break
      case 'single':
        singleValues[id] = item.defaultValue ?? ''
        break
      case 'toggle':
        booleanValues[id] = Boolean(item.defaultValue)
        break
      default:
        break
    }

    for (const dependentField of asArray(item.dependentFields)) {
      initializeFilter(dependentField)
    }
  }

  for (const item of asArray(schema)) {
    if (!item || item.type !== 'filter') continue

    initializeFilter(item)
  }

  const overrides = initialOverrides || {}
  if (overrides.textValues) Object.assign(textValues, overrides.textValues)
  if (overrides.singleValues) Object.assign(singleValues, overrides.singleValues)
  if (overrides.booleanValues) Object.assign(booleanValues, overrides.booleanValues)
  if (overrides.multiValues) {
    for (const [id, values] of Object.entries(overrides.multiValues)) {
      multiValues[id] = new Set(asArray(values))
    }
  }

  return { textValues, multiValues, singleValues, booleanValues }
}

export const usePagedRemoteList = ({
  locale,
  initialItems = [],
  initialTotalItems = 0,
  initialFilterFacets = null,
  withFacets = false,
  perPage,
  pageParam: pageParamOverride,
  debounceMs = 300,
  sortOptions = [],
  defaultSortValue,
  sortParam: sortParamOverride,
  sortParamValues = null,
  tabs = null,
  initialTabId,
  // Stan odtworzony z adresu przez serwer. Jeśli komponent serwerowy sam przeczytał `?zakladka=`,
  // `?sortowanie=` czy `?strona=` i pobrał pod to dane, przekazuje je tutaj — pierwszy render klienta
  // jest wtedy zgodny z HTML-em i lista nie miga domyślną zakładką przed docelową.
  initialActiveTabId = null,
  initialSortValue = null,
  initialPage = null,
  tabParam: tabParamOverride,
  tabParamValues = null,
  filterSchema = [],
  initialFilters,
  // Filtry w adresie, żeby przeżyły przejście w pozycję i powrót „wstecz”. Listy, które same
  // trzymają część filtrów w adresie pod inną nazwą, wyłączają je przez `urlExcludedFilterIds`.
  persistFiltersInUrl = true,
  urlExcludedFilterIds = null,
  getFilterOptionsMap,
  filterOptionsContext,
  pageUuid,
  fetcher,
  buildParams,
  selectResults,
  applyLocalSort,
  mapToItem,
  mapItems,
}) => {
  // Dane na start przychodzą z serwera, więc pierwszego zapytania nie powtarzamy.
  // Pilnuje tego porównanie z parametrami z montowania, a nie flaga „pierwsze odpalenie":
  // stan odtworzony z adresu (zakładka, sortowanie) zmienia parametry jeszcze zanim
  // zdąży wystrzelić debounce, więc flaga zjadałaby jedyny fetch i lista pokazywałaby
  // dane serwerowe pod nagłówkiem wybranej zakładki/sortowania.
  const initialRequestParamsRef = useRef(null)
  const hasFetchedRef = useRef(false)
  const pageParam = pageParamOverride === undefined ? getListQueryParamName('page', locale) : pageParamOverride
  const sortParam = sortParamOverride === undefined ? getListQueryParamName('sort', locale) : sortParamOverride
  const tabParam = tabParamOverride === undefined ? getListQueryParamName('tab', locale) : tabParamOverride
  const resolvedSortParamValues = useMemo(
    () => getLocalizedParamValues(sortParamValues, locale),
    [locale, sortParamValues],
  )
  const resolvedTabParamValues = useMemo(
    () => getLocalizedParamValues(tabParamValues, locale),
    [locale, tabParamValues],
  )

  const resolvedSortOptions = useMemo(() => asArray(sortOptions), [sortOptions])
  const defaultSort =
    resolvedSortOptions.find((option) => option.value === defaultSortValue) || resolvedSortOptions[0] || null

  const initialSort = resolvedSortOptions.find((option) => option.value === initialSortValue) || null

  const [sort, setSort] = useState(initialSort || defaultSort)
  const [page, setPage] = useState(() => resolvePageFromParamValue(initialPage) || 1)
  const isPageRestorationPendingRef = useRef(false)

  const readPageFromUrl = useCallback(() => {
    if (!pageParam || typeof window === 'undefined') return null

    return resolvePageFromParamValue(new URLSearchParams(window.location.search).get(pageParam))
  }, [pageParam])

  // Stan z adresu ustawiamy po zamontowaniu, żeby pierwszy render zgadzał się z HTML-em z serwera.
  useEffect(() => {
    const pageFromUrl = readPageFromUrl()

    if (pageFromUrl && pageFromUrl !== page) {
      isPageRestorationPendingRef.current = true
      setPage(pageFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Obsługa przycisków wstecz/dalej w przeglądarce.
  useEffect(() => {
    if (!pageParam || typeof window === 'undefined') return undefined

    const handlePopState = () => {
      const nextPage = readPageFromUrl() ?? 1

      if (nextPage === page) return

      isPageRestorationPendingRef.current = true
      setPage(nextPage)
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
  }, [page, pageParam, readPageFromUrl])

  // Sortowanie może żyć w adresie (`sortParam`) na tej samej zasadzie co zakładka —
  // dzięki temu wybór „Najstarsze” przeżywa odświeżenie strony i da się go wysłać linkiem.
  const sortValueToParamValue = useCallback(
    (value) => resolvedSortParamValues?.[value] ?? value,
    [resolvedSortParamValues],
  )
  const paramValueToSortValue = useCallback(
    (value) =>
      resolveSortValueFromParamValue(value, {
        sortOptions: resolvedSortOptions,
        paramValues: resolvedSortParamValues,
      }),
    [resolvedSortOptions, resolvedSortParamValues],
  )
  const readSortFromUrl = useCallback(() => {
    if (!sortParam || typeof window === 'undefined') return null

    const sortValue = paramValueToSortValue(new URLSearchParams(window.location.search).get(sortParam))

    return resolvedSortOptions.find((option) => option.value === sortValue) || null
  }, [paramValueToSortValue, resolvedSortOptions, sortParam])

  // Tak jak przy zakładce: stan z adresu ustawiamy dopiero po zamontowaniu,
  // inaczej pierwszy render klienta różniłby się od serwerowego (błąd hydracji).
  useEffect(() => {
    const sortFromUrl = readSortFromUrl()

    if (sortFromUrl && sortFromUrl.value !== sort?.value) {
      setSort(sortFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Obsługa przycisków wstecz/dalej w przeglądarce.
  useEffect(() => {
    if (!sortParam || typeof window === 'undefined') return undefined

    const handlePopState = () => {
      const nextSort = readSortFromUrl() ?? defaultSort

      if (nextSort?.value === sort?.value) return

      setSort(nextSort)
      if (!pageParam) setPage(1)
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSort?.value, pageParam, readSortFromUrl, sort?.value, sortParam])

  // Zakładka może żyć w adresie (`tabParam`), żeby dało się wysłać link np. do archiwum numerów.
  // Czytamy `window.location` zamiast `useSearchParams`, bo ten drugi wymusiłby otoczenie
  // każdej listy w serwisie boundary'em `Suspense`.
  const defaultTabId = tabs ? (initialTabId ?? asArray(tabs)[0]?.id ?? 'all') : undefined
  const tabIdToParamValue = useCallback((tabId) => resolvedTabParamValues?.[tabId] ?? tabId, [resolvedTabParamValues])
  const paramValueToTabId = useCallback(
    (value) => resolveTabIdFromParamValue(value, { tabs, paramValues: resolvedTabParamValues }),
    [resolvedTabParamValues, tabs],
  )
  const readTabFromUrl = useCallback(() => {
    if (!tabParam || typeof window === 'undefined') return null

    return paramValueToTabId(new URLSearchParams(window.location.search).get(tabParam))
  }, [paramValueToTabId, tabParam])

  const initialActiveTab = asArray(tabs).some((tab) => tab.id === initialActiveTabId) ? initialActiveTabId : null

  const [activeTab, setActiveTab] = useState(initialActiveTab ?? defaultTabId)

  // Zakładkę z adresu ustawiamy dopiero po zamontowaniu — inaczej pierwszy render klienta
  // różniłby się od tego z serwera i React zgłosiłby błąd hydracji.
  useEffect(() => {
    const tabFromUrl = readTabFromUrl()

    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Obsługa przycisków wstecz/dalej w przeglądarce.
  useEffect(() => {
    if (!tabParam || typeof window === 'undefined') return undefined

    const handlePopState = () => {
      const nextTabId = readTabFromUrl() ?? defaultTabId

      if (nextTabId === activeTab) return

      setActiveTab(nextTabId)
      if (!pageParam) setPage(1)
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
  }, [activeTab, defaultTabId, pageParam, readTabFromUrl, tabParam])

  const initialFilterState = useMemo(
    () => createInitialFilterState(filterSchema, initialFilters),
    [filterSchema, initialFilters],
  )
  const [openFilters, setOpenFilters] = useState(() => new Set())
  const [textValues, setTextValues] = useState(initialFilterState.textValues)
  const [multiValues, setMultiValues] = useState(initialFilterState.multiValues)
  const [singleValues, setSingleValues] = useState(initialFilterState.singleValues)
  const [booleanValues, setBooleanValues] = useState(initialFilterState.booleanValues)

  const [rawItems, setRawItems] = useState(() => asArray(initialItems))
  const [totalItems, setTotalItems] = useState(initialTotalItems)
  const [isLoading, setIsLoading] = useState(false)
  const [filterFacets, setFilterFacets] = useState(initialFilterFacets)
  const facetsKeyRef = useRef(null)
  const requestVersionRef = useRef(0)

  /**
   * Mapowanie "źródeł danych" na opcje filtrów.
   * W praktyce `filterOptionsContext` to dane pobrane na serwerze (np. wydawcy/dziedziny/języki) przekazane do klienta.
   */
  const dictionaryOptionsById = useMemo(() => {
    if (typeof getFilterOptionsMap !== 'function') return {}
    try {
      return getFilterOptionsMap({ locale, ...filterOptionsContext })
    } catch (error) {
      console.error('Failed to build filter options:', error)
      return {}
    }
  }, [getFilterOptionsMap, filterOptionsContext, locale])

  const urlExcludedFilterIdsKey = asArray(urlExcludedFilterIds).join(',')
  const filterUrlDescriptors = useMemo(
    () =>
      persistFiltersInUrl
        ? collectFilterDescriptors(filterSchema, urlExcludedFilterIdsKey ? urlExcludedFilterIdsKey.split(',') : null)
        : [],
    [filterSchema, persistFiltersInUrl, urlExcludedFilterIdsKey],
  )
  const defaultFilterState = useMemo(() => createInitialFilterState(filterSchema), [filterSchema])

  // Nadpisuje stan tylko filtrów z `ids` — pozostałe (np. wyłączone z adresu) zostają bez zmian.
  const applyFilterState = useCallback((ids, state) => {
    const pick = (values) => Object.fromEntries(Object.entries(values).filter(([id]) => ids.has(id)))

    setTextValues((prev) => ({ ...prev, ...pick(state.textValues) }))
    setMultiValues((prev) => ({ ...prev, ...pick(state.multiValues) }))
    setSingleValues((prev) => ({ ...prev, ...pick(state.singleValues) }))
    setBooleanValues((prev) => ({ ...prev, ...pick(state.booleanValues) }))
  }, [])

  const readFiltersFromUrl = useCallback(
    () =>
      readFiltersFromSearch(window.location.search, filterUrlDescriptors, {
        optionsById: dictionaryOptionsById,
        defaults: defaultFilterState,
      }),
    [defaultFilterState, dictionaryOptionsById, filterUrlDescriptors],
  )

  // Jak zakładka i sortowanie: filtry z adresu ustawiamy po zamontowaniu, bo serwer renderuje listę bez nich.
  useEffect(() => {
    if (filterUrlDescriptors.length === 0 || typeof window === 'undefined') return

    const { state, present } = readFiltersFromUrl()

    if (present.size > 0) {
      applyFilterState(present, state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Obsługa przycisków wstecz/dalej w przeglądarce.
  useEffect(() => {
    if (filterUrlDescriptors.length === 0 || typeof window === 'undefined') return undefined

    const handlePopState = () => {
      const { state } = readFiltersFromUrl()

      applyFilterState(new Set(filterUrlDescriptors.map(({ id }) => id)), state)
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
  }, [applyFilterState, filterUrlDescriptors, readFiltersFromUrl])

  // Każda zmiana filtrów trafia do adresu. replaceState, a nie pushState — „wstecz” ma wracać
  // do poprzedniej strony, a nie przeklikiwać po kolei zaznaczane filtry.
  // Pierwsze wywołanie pomijamy: widzi jeszcze stan sprzed odczytu adresu i wyczyściłoby go.
  const skipFilterUrlWriteRef = useRef(true)

  useEffect(() => {
    if (skipFilterUrlWriteRef.current) {
      skipFilterUrlWriteRef.current = false
      return
    }

    if (filterUrlDescriptors.length === 0 || typeof window === 'undefined') return

    const currentParams = new URLSearchParams(window.location.search)
    const params = writeFiltersToParams(
      new URLSearchParams(currentParams),
      filterUrlDescriptors,
      { textValues, multiValues, singleValues, booleanValues },
      defaultFilterState,
    )

    currentParams.sort()
    const query = params.toString()
    params.sort()

    if (params.toString() === currentParams.toString()) return

    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }, [booleanValues, defaultFilterState, filterUrlDescriptors, multiValues, singleValues, textValues])

  const totalPages = useMemo(() => {
    const base = perPage ? Math.ceil(totalItems / perPage) : 1
    return Math.max(1, base)
  }, [perPage, totalItems])

  useEffect(() => {
    // Przywracana strona musi najpierw pobrać właściwy zestaw danych. `totalPages` nadal opisuje wtedy SSR-ową stronę 1.
    if (page > totalPages && !isPageRestorationPendingRef.current) {
      setPage(totalPages)

      if (pageParam && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search)

        if (totalPages === 1) {
          params.delete(pageParam)
        } else {
          params.set(pageParam, String(totalPages))
        }

        const query = params.toString()
        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
      }
    }
  }, [page, pageParam, totalPages])

  /**
   * `buildParams` jest miejscem, gdzie "stan wspólny" (page/sort/filtry/tabs) zamieniasz na parametry konkretnego endpointu.
   * Dzięki temu różnice między listami są tylko w definicji, nie w UI.
   */
  const requestParams = useMemo(() => {
    if (typeof buildParams !== 'function') return null
    return buildParams({
      locale,
      page,
      perPage,
      sort,
      sortValue: sort?.value,
      activeTab,
      textValues,
      multiValues,
      singleValues,
      booleanValues,
      pageUuid,
    })
  }, [
    activeTab,
    booleanValues,
    buildParams,
    locale,
    multiValues,
    page,
    pageUuid,
    perPage,
    singleValues,
    sort,
    textValues,
  ])

  if (initialRequestParamsRef.current === null) {
    initialRequestParamsRef.current = requestParams
  }

  const facetRequestKey = getFacetRequestKey(requestParams)
  if (facetsKeyRef.current === null && initialFilterFacets) facetsKeyRef.current = facetRequestKey

  useEffect(() => {
    requestVersionRef.current += 1
  }, [requestParams])

  useDebouncedEffect(
    () => {
      // Dopóki nic nie pobraliśmy, parametry z montowania oznaczają „to już mamy z serwera".
      // Po pierwszym pobraniu pomijanie znika — powrót do stanu domyślnego też musi odświeżyć listę.
      if (
        !hasFetchedRef.current &&
        requestParams === initialRequestParamsRef.current &&
        !(withFacets && !initialFilterFacets)
      )
        return

      if (!fetcher || typeof fetcher !== 'function' || !requestParams) return

      hasFetchedRef.current = true

      const version = requestVersionRef.current
      const fetchItems = async () => {
        setIsLoading(true)
        try {
          const includeFacets = withFacets && facetsKeyRef.current !== facetRequestKey
          const response = await fetcher(withFacets ? { ...requestParams, includeFacets } : requestParams)
          if (version !== requestVersionRef.current) return
          if (includeFacets && response?.filterFacets) {
            setFilterFacets(response.filterFacets)
            facetsKeyRef.current = facetRequestKey
          }
          // `selectResults` pozwala ujednolicić różne formaty odpowiedzi (np. Hydra vs Meilisearch) do { items, totalItems }.
          const normalized = typeof selectResults === 'function' ? selectResults(response) : response
          const nextItems = asArray(normalized?.items)
          isPageRestorationPendingRef.current = false
          setRawItems(nextItems)
          setTotalItems(normalized?.totalItems || 0)
        } catch (error) {
          if (version !== requestVersionRef.current) return
          console.error('Failed to fetch list results:', error)
          isPageRestorationPendingRef.current = false
          setRawItems([])
          setTotalItems(0)
        } finally {
          if (version === requestVersionRef.current) setIsLoading(false)
        }
      }

      fetchItems()
    },
    [fetcher, requestParams, selectResults, withFacets, facetRequestKey],
    debounceMs,
  )

  /**
   * Finalne elementy do renderowania.
   * - `applyLocalSort` jest opcjonalne (czasem API nie sortuje / sort jest "lokalny"),
   * - `mapToItem` mapuje pojedynczy element (np. obiekt z API -> kafelek),
   * - `mapItems` pozwala przemapować całą listę jednocześnie (np. jeśli chcesz użyć innych danych kontekstowych).
   */
  const items = useMemo(() => {
    const sorted = typeof applyLocalSort === 'function' ? applyLocalSort(rawItems, sort?.value) : rawItems
    if (typeof mapItems === 'function') return mapItems(sorted, locale)
    if (typeof mapToItem === 'function') return sorted.map((item) => mapToItem(item, locale)).filter(Boolean)
    return sorted
  }, [applyLocalSort, locale, mapItems, mapToItem, rawItems, sort?.value])

  const removePageParamFromUrl = () => {
    isPageRestorationPendingRef.current = false

    if (!pageParam || typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    if (!params.has(pageParam)) return

    params.delete(pageParam)
    const query = params.toString()

    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    setPage(1)
    isPageRestorationPendingRef.current = false

    if (!tabParam || typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)

    if (pageParam) params.delete(pageParam)

    if (tabId === defaultTabId) {
      params.delete(tabParam)
    } else {
      params.set(tabParam, tabIdToParamValue(tabId))
    }

    const query = params.toString()

    // pushState, a nie replaceState — dzięki temu „wstecz" wraca do poprzedniej zakładki.
    window.history.pushState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }

  const handleSortValueChange = (nextValue) => {
    const nextOption = resolvedSortOptions.find((option) => option.value === nextValue)
    if (nextOption) {
      setSort(nextOption)
    }
    setPage(1)
    isPageRestorationPendingRef.current = false

    if (!nextOption || !sortParam || typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)

    if (pageParam) params.delete(pageParam)

    if (nextOption.value === defaultSort?.value) {
      params.delete(sortParam)
    } else {
      params.set(sortParam, sortValueToParamValue(nextOption.value))
    }

    const query = params.toString()

    // pushState, a nie replaceState — „wstecz” wraca do poprzedniego sortowania, tak jak przy zakładkach.
    window.history.pushState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }

  const toggleFilterOpen = (id) => {
    setOpenFilters((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const setTextValue = (id, value) => {
    setTextValues((prev) => ({ ...prev, [id]: value }))
    setPage(1)
    removePageParamFromUrl()
  }

  const toggleMultiValue = (id, value) => {
    setMultiValues((prev) => {
      const next = { ...prev, [id]: new Set(prev[id]) }
      if (next[id].has(value)) next[id].delete(value)
      else next[id].add(value)
      return next
    })
    setPage(1)
    removePageParamFromUrl()
  }

  const setMultiValue = (id, values) => {
    setMultiValues((prev) => ({ ...prev, [id]: new Set(asArray(values)) }))
    setPage(1)
    removePageParamFromUrl()
  }

  const setSingleValue = (id, value) => {
    setSingleValues((prev) => ({ ...prev, [id]: value }))
    setPage(1)
    removePageParamFromUrl()
  }

  const toggleBooleanValue = (id) => {
    setBooleanValues((prev) => ({ ...prev, [id]: !prev[id] }))
    setPage(1)
    removePageParamFromUrl()
  }

  const resetFilters = () => {
    const next = createInitialFilterState(filterSchema, initialFilters)
    setTextValues(next.textValues)
    setMultiValues(next.multiValues)
    setSingleValues(next.singleValues)
    setBooleanValues(next.booleanValues)
    setPage(1)
    removePageParamFromUrl()
  }

  const handlePageChange = (nextPage) => {
    const normalizedPage = Math.min(Math.max(nextPage, 1), totalPages)
    if (normalizedPage === page) return

    isPageRestorationPendingRef.current = false
    setPage(normalizedPage)

    if (!pageParam || typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)

    if (normalizedPage === 1) {
      params.delete(pageParam)
    } else {
      params.set(pageParam, String(normalizedPage))
    }

    const query = params.toString()

    window.history.pushState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }

  /**
   * Gotowy adapter do `FiltersPanel` – dzięki temu klient nie musi ręcznie składać obiektu z setterami.
   * Jeśli tworzysz nową listę z filtrami, przekazuj `adapter={filtersAdapter}`.
   */
  const filtersAdapter = {
    filterFacets,
    openFilters,
    toggleFilterOpen,
    textValues,
    setTextValue,
    multiValues,
    toggleMultiValue,
    setMultiValue,
    singleValues,
    setSingleValue,
    booleanValues,
    toggleBooleanValue,
    resetFilters,
  }

  const filterOptionsById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(dictionaryOptionsById).map(([id, options]) => [
          id,
          applyFilterFacets(options, filterFacets, id, multiValues[id] || (singleValues[id] ? [singleValues[id]] : [])),
        ]),
      ),
    [dictionaryOptionsById, filterFacets, multiValues, singleValues],
  )

  return {
    activeTab,
    sort,
    page,
    totalItems,
    totalPages,
    items,
    isLoading,
    filterOptionsById,
    filterFacets,
    openFilters,
    textValues,
    multiValues,
    singleValues,
    booleanValues,
    handleTabChange,
    handleSortValueChange,
    handlePageChange,
    toggleFilterOpen,
    setTextValue,
    toggleMultiValue,
    setMultiValue,
    setSingleValue,
    toggleBooleanValue,
    resetFilters,
    filtersAdapter,
  }
}
